import { useWebSocket } from '@vueuse/core'
import { webSocketUrl } from 'src/utils/domains'
import { usePatientStore } from "stores/patient";
import { useRegionStore} from "stores/region";
import { useUserStore } from "stores/user";
import {defineStore, storeToRefs} from "pinia";
import {Notify, Dialog} from "quasar";
import { useRouter, useRoute} from "vue-router";
import { watch, ref } from "vue";

export const useSocketStore = defineStore('socket', () => {
  const ws = ref(null);
  const messages = ref([]);
  const loadingHistory = ref(false);
  const simulationContext = ref(null); // { simulation_uid, course_uid, activity_uid }
  // Bumped when the server says the signed-in user was just added to the
  // running simulation, so an open StudentActivity can refetch and stop
  // treating them as a non-participant (issue 1139).
  const addedToSimulation = ref(0);
  const _historyLoaded = ref(false); // true once history has been fetched for the current sim
  const route = useRoute();
  const router = useRouter();

  // The "Simulation Started" prompt for a student who is on a chart that is not
  // their run's patient. One dialog at a time, whichever event raised it.
  let _startedDialogOpen = false;

  function showSimulationStartedDialog(courseUid, activityUid) {
    if (_startedDialogOpen) return;
    _startedDialogOpen = true;
    Dialog.create({
      title: 'Simulation Started',
      message: 'Your simulation has started. Please return to the activity to access your patient chart.',
      persistent: true
    }).onDismiss(() => {
      _startedDialogOpen = false;
      if (courseUid && activityUid) {
        router.push(`/student/course/${courseUid}/activity/${activityUid}`)
      } else {
        router.push('/student')
      }
    })
  }

  // True when the socket belongs to this activity and the student is looking at
  // a chart (/patient/...) that is not a patient of run `simUid`: the template
  // chart they opened before the run existed, or a preview. A run's own patients
  // carry the run's simulation_uid; an unloaded chart is left alone.
  function onNonRunChart(activityUid, simUid) {
    const ctx = simulationContext.value;
    if (!ctx || ctx.activity_uid !== activityUid || !route.path.startsWith('/patient/')) return false;
    const {patient} = storeToRefs(usePatientStore());
    return !!patient.value && patient.value.simulation_uid !== simUid;
  }

  // Run `simUid` has just started on the activity this socket belongs to. If the
  // signed-in student holds participant rows for it but is sitting on a chart
  // that is not the run's, send them to the activity. Evaluated once per run: a
  // student who is not a participant is never prompted, and a later add is
  // announced by its own `added` event. A check that failed is not recorded, so
  // the next activity event for the run tries again.
  const _startedRunsChecked = new Set();

  function promptStartedParticipant(activityUid, simUid) {
    if (_startedRunsChecked.has(simUid) || !onNonRunChart(activityUid, simUid)) return;
    _startedRunsChecked.add(simUid);
    const {course_uid: courseUid} = simulationContext.value;
    useRegionStore().GetStudentActivity(courseUid, activityUid).then((response) => {
      // The server's answer (a row carrying this run's uid), not inferred from
      // `patients`, which are attempt rows without a simulation_uid for a
      // require_submission activity with several attempts.
      const participant = response?.is_simulation_participant === true && response.activity?.current_sim?.uid === simUid;
      // The student may have moved while the request ran.
      if (participant && onNonRunChart(activityUid, simUid)) {
        showSimulationStartedDialog(courseUid, activityUid);
      }
    }).catch((error) => {
      _startedRunsChecked.delete(simUid);
      console.error('Failed to check simulation participation:', error);
    });
  }

  function addMessage(message) {
    // History and the live broadcast can deliver the same saved message, in either order.
    // Lines the page builds itself carry no uid and are always added.
    if (message.uid && messages.value.some(m => m.uid === message.uid)) return;
    messages.value = [...messages.value, message];
  }

  function clearMessages() {
    messages.value = [];
  }

  function sendChatMessage(content, simulationUid, activityUid) {
    if (ws.value && ws.value.status === 'OPEN') {
      ws.value.send(JSON.stringify({
        action: 'sendMessage',
        content: content,
        simulation_uid: simulationUid,
        activity_uid: activityUid,
      }));
    }
  }

  function connect_sim(simulation_uid, course_uid, activity_uid, organization_uid) {
    console.log('Connect Sim Called')
    const prevCtx = simulationContext.value;
    simulationContext.value = { simulation_uid, course_uid, activity_uid, organization_uid };
    if (!ws.value || ws.value.status === 'CLOSED') {
      clearMessages();
      _historyLoaded.value = false;
      //Get the Region
      const regionStore = useRegionStore();
      regionStore.GetOrganization(organization_uid).then(() => {
        const {org} = storeToRefs(regionStore);
        // Stage-level ws domain (manually owned, like api.{stage}) — its
        // api-mapping flips between the v2 and SST4 websocket APIs without
        // touching the CFN-owned regional domain. Region routing, if ever
        // needed, moves behind this hostname.
        console.log('Creating WebSocket at ' + webSocketUrl())
        const that = this;
        ws.value = useWebSocket(webSocketUrl(),
          {
            immediate: true,
            autoClose: false,
            autoReconnect: {
              retries: -1,
              delay: 1000,
              onFailed() {
                console.log('Failed to connect WebSocket')
                ws.value.close()
              },
            },
            heartbeat: {
                message: JSON.stringify({action: 'heartbeat'}),
                interval: 60000,
                pongTimeout: 10000,
            },
            onConnected() {
              console.log('WebSocket connected!')
              // Use current simulationContext (reactive ref) instead of closed-over
              // function params so reconnects use the latest simulation, not the
              // one that was active when the WebSocket was first created.
              const ctx = simulationContext.value;
              if (!ctx) return;

              const userStore = useUserStore();
              const {user} = storeToRefs(userStore);
              ws.value.send(JSON.stringify({
                action: 'connect_sim',
                simulation_uid: ctx.simulation_uid,
                course_uid: ctx.course_uid,
                activity_uid: ctx.activity_uid,
                user_embed: {
                  uid: user.value.uid,
                  first_name: user.value.first_name,
                  last_name: user.value.last_name,
                  email: user.value.email,
                  picture: user.value.picture,
                  initials: user.value.initials,
                  organization_uid: ctx.organization_uid
                }
              }))

              // Only load message history on the first connection for this
              // simulation. Reconnects already have messages in memory and
              // will receive new ones via the WebSocket — no need to re-fetch.
              if (ctx.simulation_uid && !_historyLoaded.value) {
                _historyLoaded.value = true;
                const regionStore = useRegionStore();
                loadingHistory.value = true;
                regionStore.GetSimulationMessages(ctx.course_uid, ctx.activity_uid, ctx.simulation_uid).then((msgs) => {
                  if (msgs && msgs.length) {
                    clearMessages();
                    msgs.forEach(m => addMessage(m));
                  }
                }).catch((e) => {
                  console.error('Failed to load message history:', e);
                }).finally(() => {
                  loadingHistory.value = false;
                });
              }
            },
            onDisconnected(sock, evt) {
              console.log('WebSocket disconnected!')
              console.log(evt)
            },
            onMessage(websocket, e) {
              const message = JSON.parse(e.data);
              console.log(message);

              const patientStore = usePatientStore();
              const {students, activity, selected_sim, patient, viewing_visit} = storeToRefs(patientStore);
              const userStore = useUserStore();
              const {user} = storeToRefs(userStore);

              // Presence (connect/disconnect) must only update the roster for
              // the simulation currently being viewed. A user connects with a
              // null simulation when they are not a participant (e.g. a student
              // just opening the activity); the backend broadcasts that to every
              // connection on the activity, so without this guard a null-sim
              // connect would leak into the running sim's user list. Compare the
              // event's simulation_uid against the selected sim (null === null
              // for the pre-sim / not-yet-started view).
              const selectedSimUid = selected_sim.value ? selected_sim.value.uid : null;
              if (message.connect) {
                const connectSimUid = message.connect.simulation_uid || null;
                if (connectSimUid === selectedSimUid) {
                  if (!students.value.find(s => s.uid === message.connect.user_embed.uid)) {
                    students.value.push(message.connect.user_embed);
                  }
                  else{
                    students.value = students.value.map(s => {
                      if (s.uid === message.connect.user_embed.uid) {
                        s = message.connect.user_embed;
                      }
                      return s;
                    });
                  }
                }
              }
              if (message.disconnect) {
                const disconnectSimUid = message.disconnect.simulation_uid || null;
                if (disconnectSimUid === selectedSimUid) {
                  students.value = students.value.map(s => {
                    if (s.uid === message.disconnect.user_embed.uid) {
                      s = message.disconnect.user_embed;
                    }
                    return s;
                  });
                }
              }
              if (message.added && user.value && message.added.user_uid === user.value.uid) {
                addedToSimulation.value++;
                // An open StudentActivity refetches on the bump above. A student
                // on a chart that is not the run's has no such page to refresh:
                // tell them and take them to the activity.
                if (userStore.default_route === '/student' && onNonRunChart(message.added.activity_uid, message.added.simulation_uid)) {
                  showSimulationStartedDialog(message.added.course_uid, message.added.activity_uid);
                }
              }
              if (message.removed) {
                students.value = students.value.filter(s => s.uid !== message.removed.user_embed.uid);
              }

              // `left` is a socket trace being cleaned up, not an instructor's
              // Remove: it drops the entry from the roster of the simulation being
              // viewed (a trace under another key must not take the student out of
              // this one) and tells nobody they were removed.
              if (message.left && (message.left.simulation_uid || null) === selectedSimUid) {
                students.value = students.value.filter(s => s.uid !== message.left.user_embed.uid);
              }

              // Only the instructor's Remove (`removed`) for this page's activity. The
              // false boots were null-keyed trace cleanups reaching a page that was in
              // a run, so a null-keyed removal counts only for a page with no run
              // context; a run-keyed `removed` can only be the instructor's Remove
              // (even for a participant whose page context is still null).
              const removalIsForThisPage = !!message.removed && !!simulationContext.value
                && message.removed.activity_uid === simulationContext.value.activity_uid
                && (!!message.removed.simulation_uid || !simulationContext.value.simulation_uid);
              if (removalIsForThisPage && userStore.default_route === '/student' && user.value.uid === message.removed.user_embed.uid) {
                console.log("Removing User")
                ws.value.close();
                const d = Dialog.create({
                  title: 'You have been removed',
                  message: 'You have been removed from this simulation. You will be returned to the student page.'
                }).onDismiss(() => {
                  router.push(`/student`)
                })
              }

              if (message.completed && userStore.default_route === '/student' && simulationContext.value && simulationContext.value.simulation_uid) {
                ws.value.close();
                const d = Dialog.create({
                  title: 'Simulation Completed',
                  message: 'This simulation has ended. You will be returned to the student page.'
                }).onDismiss(() => {
                  router.push(`/student`)
                })
              }
              if (message.activity && activity.value && activity.value.uid === message.activity.uid) {
                // Preserve _original_patient_type on template_patients (frontend-only property)
                if (activity.value.template_patients && message.activity.template_patients) {
                  for (const tp of message.activity.template_patients) {
                    const existing = activity.value.template_patients.find(p => p.uid === tp.uid);
                    if (existing && existing._original_patient_type) {
                      tp._original_patient_type = existing._original_patient_type;
                    }
                  }
                }
                // The broadcast leaves the description fields out (they can exceed the
                // socket size limit), so keep the copies this page already holds.
                activity.value = {
                  instructor_description: activity.value.instructor_description,
                  student_instructions: activity.value.student_instructions,
                  ...message.activity,
                };
                // Sync selected_sim when the activity message includes an updated current_sim
                if (message.activity.current_sim && selected_sim.value && selected_sim.value.uid === message.activity.current_sim.uid) {
                  selected_sim.value = message.activity.current_sim;
                }
              }
              // Start emits only this activity event (never a simulation_run), so it
              // is the signal that a run has begun for a student whose page is not
              // the activity page.
              if (message.activity && message.activity.current_sim && message.activity.current_sim.status === 'running' && userStore.default_route === '/student') {
                promptStartedParticipant(message.activity.uid, message.activity.current_sim.uid);
              }
              if (message.simulation_run && selected_sim.value && selected_sim.value.uid === message.simulation_run.uid) {
                selected_sim.value = message.simulation_run;
              }
              if (message.simulation_run && activity.value && activity.value.current_sim && activity.value.current_sim.uid === message.simulation_run.uid) {
                activity.value.current_sim = message.simulation_run;
              }
              // When a simulation starts for the first time, current_sim is null so the UID check above won't match.
              // Set current_sim from the simulation_run message if the activity matches and current_sim isn't set yet.
              if (message.simulation_run && activity.value && !activity.value.current_sim && message.simulation_run.activity_uid === activity.value.uid && message.simulation_run.status === 'running') {
                activity.value.current_sim = message.simulation_run;
              }
              if (message.simulation_run && patient.value && patient.value.activity && patient.value.activity.current_sim && patient.value.activity.current_sim.uid === message.simulation_run.uid) {
                patient.value.activity.current_sim = message.simulation_run;
              }
              // Same fix for patient context: set current_sim when it's null on initial sim start
              if (message.simulation_run && patient.value && patient.value.activity && !patient.value.activity.current_sim && message.simulation_run.activity_uid === patient.value.activity.uid && message.simulation_run.status === 'running') {
                patient.value.activity.current_sim = message.simulation_run;
              }
              if (message.simulation_run && message.simulation_run.status === 'running') {
                // Only show "Simulation Started" dialog/notification for initial start, not resume or time warp
                const action = message.simulation_action
                if (action === 'resumed' || action === 'time_warp') {
                  // Resume and time warp don't need the start dialog
                } else if (userStore.default_route === '/student' && patient.value && patient.value.patient_type === 'preview') {
                  showSimulationStartedDialog(
                    patient.value.course_uid || route.query.course_uid,
                    patient.value.activity_uid || route.query.activity_uid
                  )
                } else {
                  Notify.create({
                    color: 'positive',
                    message: 'Simulation has started',
                    timeout: 5000,
                    actions: [
                      { icon: 'close', color: 'white', round: true, handler: () => {} }
                    ]
                  })
                }
              }
              if (message.chat_message) {
                addMessage(message.chat_message);
                const sender = message.chat_message.sender_embed;
                if (sender && sender.uid !== user.value.uid) {
                  const senderName = [sender.first_name, sender.last_name].filter(Boolean).join(' ') || 'Someone';
                  const preview = message.chat_message.content?.length > 80
                    ? message.chat_message.content.substring(0, 80) + '…'
                    : message.chat_message.content;
                  Notify.create({
                    color: 'info',
                    message: `${senderName}: ${preview}`,
                    timeout: 3000,
                    actions: [
                      { icon: 'close', color: 'white', round: true, handler: () => {} }
                    ]
                  });
                }
              }
              if (message.system_message) {
                addMessage(message.system_message);
              }
              if (message.notification_message) {
                addMessage(message.notification_message);
              }
              if (message.patient) {
                // Add to chat panel as a notification message
                const patientName = message.patient.legal_name || message.patient.name || 'Unknown patient';
                const meta = { patient_uid: message.patient.uid };
                if (message.scene_uid) {
                  // Surface scene_uid so per-patient consumers (e.g. the
                  // OrchestrateXR virtual-patient panel) can fetch the scene's
                  // context and dedup on it. Additive metadata only — the
                  // existing system-message content and behavior are unchanged.
                  meta.scene_uid = message.scene_uid;
                  addMessage({ message_type: 'system', content: `Event added to ${patientName}`, metadata: meta, created_at: new Date().toISOString() });
                } else if (message.flowsheet) {
                  meta.flowsheet_uid = message.flowsheet.uid;
                  meta.visit_uid = message.visit_uid;
                  addMessage({ message_type: 'notification', content: `${patientName}: ${message.flowsheet.flowsheet_name} updated`, metadata: meta, created_at: new Date().toISOString() });
                } else if (message.visit) {
                  meta.visit_uid = message.visit.uid;
                  addMessage({ message_type: 'notification', content: `${patientName}: visit updated`, metadata: meta, created_at: new Date().toISOString() });
                } else {
                  addMessage({ message_type: 'notification', content: `Patient updated: ${patientName}`, metadata: meta, created_at: new Date().toISOString() });
                }

                let sim_value = undefined;
                if(route.query.simulation) {
                  sim_value = route.query.simulation;
                } else if(route.path.includes('/sim')) {
                  sim_value = 'control';
                }

                if (message.visit) {
                  Notify.create({
                    color: 'info',
                    message: `${message.patient.name} visit has been updated`,
                    timeout: 10000,
                    actions: [
                      {
                        label: 'View',
                        flat: false,
                        color: 'primary',
                        to: {path: `/patient/${message.patient.uid}/visit/${message.visit.uid}`, query:
                           {
                          simulation: sim_value, visit_uid: message.visit.uid
                        }
                        }
                      },
                      {
                        icon: 'close', color: 'white', round: true, handler: () => { /* ... */
                        }
                      }]
                  })
                } else if (message.scene_uid) {
                  Notify.create({
                    color: 'info',
                    message: `${message.patient.name} has been updated`,
                    timeout: 10000,
                    actions: [
                      {
                        label: 'View',
                        flat: false,
                        color: 'primary',
                        to: {path: `/patient/${message.patient.uid}`, query: {
                          simulation: sim_value, visit_uid: message.visit_uid
                        }
                        }
                      },
                      {
                        icon: 'close', color: 'white', round: true, handler: () => { /* ... */
                        }
                      }]
                  })
                } else if (message.flowsheet) {
                  Notify.create({
                    color: 'info',
                    message: `${message.patient.name} ${message.flowsheet.flowsheet_name} has been updated`,
                    timeout: 10000,
                    actions: [
                      {
                        label: 'View',
                        flat: false,
                        color: 'primary',
                        to: {path: `/patient/${message.patient.uid}/flowsheet/${message.flowsheet.uid}`, query:
                            {simulation: sim_value, visit_uid: message.visit_uid}
                        }
                      },
                      {
                        icon: 'close', color: 'white', round: true, handler: () => { /* ... */
                        }
                      }]
                  })
                } else {
                  Notify.create({
                    color: 'info',
                    message: `${message.patient.name} has been updated`,
                    timeout: 10000,
                    actions: [
                      {
                        label: 'View',
                        flat: false,
                        color: 'primary',
                        to: {path: `/patient/${message.patient.uid}`,
                          query: {
                          simulation: sim_value, visit_uid: message.visit_uid
                        }
                        }
                      },
                      {
                        icon: 'close', color: 'white', round: true, handler: () => { /* ... */
                        }
                      }]
                  })
                }
              }
              if (message.patient && patient.value && patient.value.uid === message.patient.uid) {
                if (message.scene_uid) {
                  console.log('Event Added')
                }
                if (message.visit) {
                  if (viewing_visit.value && viewing_visit.value.uid === message.visit.uid) {
                    console.log('Visit Update')
                    viewing_visit.value = message.visit;
                  }
                }
                if (message.flowsheet) {
                  console.log('Flowsheet Update');
                  console.log(message.flowsheet);
                }
                patient.value = {...patient.value, ...message.patient};
              }
            }
          });
      }).catch((error) => {
        // GetOrganization failed (network drop, backend error) before the
        // WebSocket could even be created, so the .then() above never runs and
        // the simulation is left silently unconnected with no ws.value at all
        // (issue #1189). error_intercept's toast on the underlying request is
        // the only user-facing message for this failure -- restapi.js is the
        // one place that shows it (house rule, 2026-09-29); this .catch only
        // logs and leaves ws.value/simulationContext in their pre-connect
        // state so a later retry can still run connect_sim from scratch.
        console.error('Failed to connect simulation: could not load organization', error);
      });
    } else {
      // Skip if we're already connected to this exact simulation
      if (prevCtx && prevCtx.simulation_uid === simulation_uid && prevCtx.course_uid === course_uid && prevCtx.activity_uid === activity_uid) {
        return;
      }

      console.log('WebSocket already created, switching simulation context');
      // Set loadingHistory BEFORE clearing messages so the unread-count
      // watcher in App.vue does not mistakenly increment on the clear.
      loadingHistory.value = true;
      _historyLoaded.value = false;
      clearMessages();

      // Re-register with the backend for the new simulation
      const userStore = useUserStore();
      const {user} = storeToRefs(userStore);
      ws.value.send(JSON.stringify({
        action: 'connect_sim',
        simulation_uid: simulation_uid,
        course_uid: course_uid,
        activity_uid: activity_uid,
        user_embed: {
          uid: user.value.uid,
          first_name: user.value.first_name,
          last_name: user.value.last_name,
          email: user.value.email,
          picture: user.value.picture,
          initials: user.value.initials,
          organization_uid: organization_uid
        }
      }));

      // Load message history for the new simulation
      if (simulation_uid) {
        const regionStore = useRegionStore();
        regionStore.GetSimulationMessages(course_uid, activity_uid, simulation_uid).then((msgs) => {
          if (msgs && msgs.length) {
            clearMessages();
            msgs.forEach(m => addMessage(m));
          }
        }).catch((e) => {
          console.error('Failed to load message history:', e);
        }).finally(() => {
          loadingHistory.value = false;
        });
      } else {
        loadingHistory.value = false;
      }
    }
  }

  watch(route, (to, from) => {
    if(!(to.path.includes('/activity') || to.path.includes('/patient') || to.path.includes('/sim'))) {
      if (ws.value) {
        ws.value.close()
      }
      clearMessages();
      simulationContext.value = null;
      _historyLoaded.value = false;
    }
  })

  return {ws, messages, loadingHistory, simulationContext, addedToSimulation, connect_sim, addMessage, sendChatMessage, clearMessages}
});
